import { Modal as MantineModal, NativeScrollArea } from '@mantine/core';
import { MODAL_ZINDEX } from '@zorro/types';
import type { ReactNode } from 'react';

import { brand } from '../../theme';
import { CloseButton } from '../CloseButton';
import styles from './Modal.module.css';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback fired when the modal is closed (overlay click, escape, close button) */
  onClose: () => void;
  /** Modal title text */
  title?: string;
  /** Flexible body content rendered below the title */
  children?: ReactNode;
  /** Footer content (e.g. buttons), pinned to bottom */
  footer?: ReactNode;
  /** Modal content width (default: 1320) */
  size?: string | number;
  /** Whether the modal is centered vertically (default: true) */
  isCentered?: boolean;
  /** Whether to close the modal when clicking outside */
  shouldCloseOnClickOutside?: boolean;
  /** Whether to close the modal when pressing Escape */
  shouldCloseOnEscape?: boolean;
  /** Additional class name for the root element */
  className?: string;
  /** Minimum height of the modal body — useful when footer should be pinned at the bottom */
  minHeight?: string | number;
  /** Hide the built-in close button (use when the consumer provides its own close mechanism) */
  isCloseButtonHidden?: boolean;
  /** Override the default z-index (use when stacking above another modal) */
  zIndex?: number;
  /** Render children edge-to-edge (no header/body padding). Close button floats top-right over content. */
  hasNoPadding?: boolean;
  /** Cap the modal at the viewport height and scroll only the body, keeping the title and footer pinned */
  hasScrollableBody?: boolean;
}

const MODAL_CLASS_NAMES = {
  content: styles.modalContent,
  body: styles.modalBody,
};

const OVERLAY_PROPS = {
  color: brand.zorroCoal900,
  opacity: 0.9,
  blur: 12,
};

const TRANSITION_PROPS = { transition: 'fade' as const };

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 1320,
  isCentered = true,
  shouldCloseOnClickOutside,
  shouldCloseOnEscape,
  className,
  minHeight,
  isCloseButtonHidden = false,
  zIndex = MODAL_ZINDEX,
  hasNoPadding = false,
  hasScrollableBody = false,
}: ModalProps) {
  return (
    <MantineModal
      opened={isOpen}
      onClose={onClose}
      withCloseButton={false}
      centered={isCentered}
      size={size}
      closeOnClickOutside={shouldCloseOnClickOutside}
      closeOnEscape={shouldCloseOnEscape}
      classNames={MODAL_CLASS_NAMES}
      scrollAreaComponent={NativeScrollArea}
      overlayProps={OVERLAY_PROPS}
      transitionProps={TRANSITION_PROPS}
      zIndex={zIndex}
    >
      <div
        className={`${styles.root} ${className ?? ''}`}
        data-no-padding={hasNoPadding || undefined}
        data-scrollable-body={hasScrollableBody || undefined}
        style={minHeight !== undefined ? { minHeight } : undefined}
      >
        <div className={styles.header}>
          <div className={styles.titleContainer}>
            {title && <h2 className={styles.title}>{title}</h2>}
            {children && <div className={styles.body}>{children}</div>}
          </div>
          {!isCloseButtonHidden && (
            <CloseButton
              onClick={onClose}
              aria-label="Close"
              className={styles.closeButton}
            />
          )}
        </div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </MantineModal>
  );
}

Modal.displayName = 'Modal';
